import { create } from 'zustand'

import type { AuthSession, AuthStatus, AuthUser } from '@/types/auth'

/**
 * Estado de sessão do cliente.
 *
 * O access token vive APENAS em memória (aqui) — nunca em localStorage/
 * sessionStorage, para não ser legível por script injetado via XSS.
 * O refresh token é um cookie httpOnly gerido pela API; o front nunca o toca.
 */
interface AuthState {
  accessToken: string | null
  /** Expiração do access token em epoch ms (para renovação proativa). */
  accessTokenExpiresAt: number | null
  user: AuthUser | null
  status: AuthStatus
  /** Login com senha sem vínculo de biometria: o app oferece o cadastro do aparelho. */
  ofertarBiometria: boolean

  setSession: (session: AuthSession) => void
  clearSession: () => void
  markUnauthenticated: () => void
  /** Sessão travada até o desbloqueio por biometria; o cookie de refresh continua válido. */
  lock: () => void
  setOfertarBiometria: (ofertar: boolean) => void
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  accessTokenExpiresAt: null,
  user: null,
  // Começa "authenticating": o bootstrap tenta restaurar a sessão via /auth/refresh.
  status: 'authenticating',
  ofertarBiometria: false,

  setSession: (session) =>
    set({
      accessToken: session.accessToken,
      accessTokenExpiresAt: new Date(session.accessTokenExpiresAtUtc).getTime(),
      user: session.usuario,
      status: 'authenticated',
    }),

  clearSession: () =>
    set({
      accessToken: null,
      accessTokenExpiresAt: null,
      user: null,
      status: 'unauthenticated',
      ofertarBiometria: false,
    }),

  lock: () => set({ accessToken: null, accessTokenExpiresAt: null, user: null, status: 'locked' }),

  setOfertarBiometria: (ofertarBiometria) => set({ ofertarBiometria }),

  markUnauthenticated: () => set({ status: 'unauthenticated' }),
}))

/** Acesso fora de componentes React (ex.: interceptors do httpClient). */
export const authStore = {
  getState: useAuthStore.getState,
  setState: useAuthStore.setState,
}
